import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { RotateCcw, AlertCircle } from "lucide-react";

/**
 * Admin-only modal to override the commission amount on a single event.
 * Cascades to recompute the parent payout total. Refuses on events attached
 * to already-paid payouts (backend returns 409).
 *
 * Props:
 *   event    — the event object (must include id, commission_amount, and
 *              optionally original_commission_amount + override_reason).
 *   onClose  — modal dismiss handler
 *   onSaved  — invoked after successful save/clear (caller reloads data)
 */
export default function OverrideEventModal({ event, onClose, onSaved }) {
  const isOverridden = event?.original_commission_amount != null;
  const originalAmount = isOverridden
    ? Number(event.original_commission_amount)
    : Number(event?.commission_amount || 0);
  const [amount, setAmount] = useState(
    Number(event?.commission_amount || 0).toFixed(2)
  );
  const [reason, setReason] = useState(event?.override_reason || "");
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    const nextAmount = Number(amount);
    if (Number.isNaN(nextAmount) || nextAmount < 0) {
      toast.error("Enter a valid non-negative amount");
      return;
    }
    if (nextAmount.toFixed(2) === Number(event.commission_amount || 0).toFixed(2)) {
      toast.info("No change to save");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please provide a reason for the override");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/commissions/events/${event.id}`, {
        commission_amount: nextAmount,
        override_reason: reason.trim(),
      });
      toast.success("Commission overridden — payout total recalculated");
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Override failed"
      );
    } finally {
      setBusy(false);
    }
  }

  async function clearOverride() {
    if (!window.confirm("Revert to the original commission amount?")) return;
    setBusy(true);
    try {
      await api.post(`/commissions/events/${event.id}/clear-override`);
      toast.success("Override cleared — original amount restored");
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Clear failed"
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="override-event-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-800">
          <h3 className="font-display text-xl">Override commission</h3>
          <div className="text-xs text-slate-500 mt-1">
            Manually adjust the commission on this line. The parent payout
            total will recalculate automatically.
          </div>
        </div>

        {event?.payout_id && (
          <div className="mx-6 mt-4 rounded-lg border border-amber-800/60 bg-amber-950/40 p-3 flex items-start gap-2 text-xs text-amber-200">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              This event is attached to payout{" "}
              <span className="font-mono">
                #{event.payout_id.slice(0, 8)}
              </span>
              . The override will re-total that payout. Paid payouts are
              locked.
            </span>
          </div>
        )}

        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Original
              </div>
              <div
                data-testid="override-original-amount"
                className="font-mono text-lg mt-1 text-slate-300"
              >
                {money(originalAmount)}
              </div>
            </div>
            <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-4">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">
                Current
              </div>
              <div className="font-mono text-lg mt-1 text-cyan-300">
                {money(Number(event?.commission_amount || 0))}
              </div>
            </div>
          </div>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              New commission amount (ex-GST)
            </span>
            <input
              data-testid="override-amount-input"
              required
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-lg border border-slate-800 bg-slate-950 text-slate-100 px-3 py-2.5 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 font-mono text-base"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Reason for override <span className="text-rose-400">*</span>
            </span>
            <textarea
              data-testid="override-reason-input"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Deducted $50 for setup credit, agreed with partner"
              className="w-full rounded-lg border border-slate-800 bg-slate-950 text-slate-100 placeholder:text-slate-600 px-3 py-2.5 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 text-sm"
            />
          </label>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-800">
            {isOverridden ? (
              <button
                data-testid="override-clear-btn"
                type="button"
                onClick={clearOverride}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-400 hover:text-rose-300"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Revert to original
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950"
              >
                Cancel
              </button>
              <button
                data-testid="override-save-btn"
                type="submit"
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save override"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
